import IAddManufacturedDeviceRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/add-manufactured-device-request-dto";
import IUpsertRegisterDefinitionRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/upsert-register-definition-request-dto";
import IAddManufacturedDeviceResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/add-manufactured-device-response-dto";
import IGetAllModbusDeviceResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-all-modbus-device-response-dto";
import IGetAllModbusDevicesResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-all-modbus-devices-response-dto";
import IGetDeviceTelemetryHistoryResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-device-telemetry-history-response-dto";
import IGetLiveDeviceTelemetryResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-live-device-telemetry-response-dto";
import IGetRawDeviceTelemetryResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-raw-device-telemetry-response-dto";
import IGetRegisterDefinitionResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-register-definition-response-dto";
import IListManufacturedDevicesResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/list-manufactured-deevices-response-dto";
import IUpsertRegisterDefinitionResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/upsert-register-definition-response-dto";
import IViewExpandedManufacturedDeviceResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-expanded-manufactured-device-response-dto";
import { EManufacturedSmartDeviceType } from "../../../shared/oversight-core/enums/manufactured-smart-device-type";
import { transformErrorResponse } from "../../../shared/oversight-core/utils/transformErrorResponse";
import { rootApi } from "../apiManager";

export const modBusAPI = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    addManufactureDevice: builder.mutation<
      IAddManufacturedDeviceResponseDTO,
      IAddManufacturedDeviceRequestDTO
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: `smart-device/manufactured-device/`,
          method: "POST",
          body: {
            deviceId: data.deviceId,
            smartDeviceType: EManufacturedSmartDeviceType.MODBUS_READER,
          },
        };
      },
    }),
    viewManufacturedDeviceList: builder.query<
      IListManufacturedDevicesResponseDTO,
      { smartDeviceType: EManufacturedSmartDeviceType }
    >({
      transformErrorResponse,
      query({ smartDeviceType }) {
        return {
          url: `smart-device/manufactured-device/list`,
          method: "GET",
          params: { smartDeviceType },
        };
      },
    }),
    viewModBusManufacturedDeviceBySerialKey: builder.query<
      IViewExpandedManufacturedDeviceResponseDTO,
      { serialKey: string }
    >({
      transformErrorResponse,
      query({ serialKey }) {
        return {
          url: `smart-device/manufactured-device/${serialKey}/connected`,
          method: "GET",
        };
      },
    }),
    registerDefinition: builder.mutation<
      IUpsertRegisterDefinitionResponseDTO,
      IUpsertRegisterDefinitionRequestDTO
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: `modbus/devices/${data.partitionKey}/registers`,
          method: "PUT",
          body: data,
        };
      },
    }),
    viewDefinitionByKey: builder.query<
      IGetRegisterDefinitionResponseDTO,
      { partitionKey: string }
    >({
      transformErrorResponse,
      query({ partitionKey }) {
        return {
          url: `modbus/devices/${partitionKey}/registers`,
          method: "GET",
        };
      },
    }),
    viewModBusDevices: builder.query<
      IGetAllModbusDevicesResponseDTO,
      { deviceId?: string }
    >({
      transformErrorResponse,
      query({ deviceId }) {
        return {
          url: `modbus/devices`,
          method: "GET",
          params: { deviceId },
        };
      },
    }),
    viewModBusDevicesByPartitionKey: builder.query<
      IGetAllModbusDeviceResponseDTO,
      { partitionKey: string }
    >({
      transformErrorResponse,
      query({ partitionKey }) {
        return {
          url: `modbus/devices/${partitionKey}`,
          method: "GET",
        };
      },
    }),
    viewModBusTelemetryRawByPartitionKey: builder.query<
      IGetRawDeviceTelemetryResponseDTO,
      { partitionKey: string }
    >({
      transformErrorResponse,
      query({ partitionKey }) {
        return {
          url: `modbus/devices/${partitionKey}/telemetry/raw`,
          method: "GET",
        };
      },
    }),
    viewModBusTelemetryLiveByPartitionKey: builder.query<
      IGetLiveDeviceTelemetryResponseDTO,
      { partitionKey: string }
    >({
      transformErrorResponse,
      query({ partitionKey }) {
        return {
          url: `modbus/devices/${partitionKey}/telemetry/live`,
          method: "GET",
        };
      },
    }),
    viewModBusTelemetryHistoryByPartitionKey: builder.query<
      IGetDeviceTelemetryHistoryResponseDTO,
      { partitionKey: string; from: string; to: string; graphType: string }
    >({
      transformErrorResponse,
      query({ partitionKey, from, to, graphType }) {
        return {
          url: `modbus/devices/${partitionKey}/telemetry/history`,
          method: "GET",
          params: { from, to, graphType },
        };
      },
    }),
    deleteRegistersDefinitions: builder.mutation<
      void,
      { partitionKey: string }
    >({
      transformErrorResponse,
      query({ partitionKey }) {
        return {
          url: `modbus/devices/${partitionKey}/registers`,
          method: "DELETE",
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddManufactureDeviceMutation,
  useLazyViewManufacturedDeviceListQuery,
  useLazyViewModBusManufacturedDeviceBySerialKeyQuery,
  useRegisterDefinitionMutation,
  useLazyViewDefinitionByKeyQuery,
  useLazyViewModBusDevicesQuery,
  useViewModBusDevicesByPartitionKeyQuery,
  useLazyViewModBusTelemetryRawByPartitionKeyQuery,
  useLazyViewModBusTelemetryLiveByPartitionKeyQuery,
  useLazyViewModBusTelemetryHistoryByPartitionKeyQuery,
  useDeleteRegistersDefinitionsMutation,
} = modBusAPI;
